import {
  PmfDisappointment,
  PmfQuestionType,
  PmfStepId,
  PmfSurveyFreeTextEvent,
} from '@griply/types';
import { getPmfNextStepId, PMF_SURVEY_CONFIG } from '@griply/utils';
import { useCallback, useEffect, useState } from 'react';

import { useLatestRef } from './use-latest-ref';

export const THANK_YOU_DISMISS_MS = 3000;

export type UsePmfSurveyStepsOptions = {
  answerDisappointment: (value: PmfDisappointment) => void;
  answerText: (event: PmfSurveyFreeTextEvent, value: string) => void;
  onFinished: () => void;
};

export const usePmfSurveySteps = ({
  answerDisappointment,
  answerText,
  onFinished,
}: UsePmfSurveyStepsOptions) => {
  const [stepId, setStepId] = useState<PmfStepId>(
    PMF_SURVEY_CONFIG.firstStepId,
  );
  const [disappointment, setDisappointment] =
    useState<PmfDisappointment | null>(null);
  const [showThankYou, setShowThankYou] = useState(false);
  const callbacksRef = useLatestRef({
    answerDisappointment,
    answerText,
    onFinished,
  });

  const step = PMF_SURVEY_CONFIG.steps[stepId];

  const advanceFrom = useCallback(
    (fromStepId: PmfStepId, answer: PmfDisappointment | null) => {
      const nextStepId = getPmfNextStepId(fromStepId, answer);
      nextStepId ? setStepId(nextStepId) : setShowThankYou(true);
    },
    [],
  );

  const selectOption = useCallback(
    (value: PmfDisappointment) => {
      setDisappointment(value);
      callbacksRef.current.answerDisappointment(value);
      advanceFrom(PMF_SURVEY_CONFIG.firstStepId, value);
    },
    [advanceFrom, callbacksRef],
  );

  const submitText = useCallback(
    (value: string) => {
      const currentStep = PMF_SURVEY_CONFIG.steps[stepId];
      if (currentStep.type !== PmfQuestionType.FreeText) {
        return;
      }
      callbacksRef.current.answerText(currentStep.event, value);
      advanceFrom(stepId, disappointment);
    },
    [stepId, disappointment, advanceFrom, callbacksRef],
  );

  const reset = useCallback(() => {
    setStepId(PMF_SURVEY_CONFIG.firstStepId);
    setDisappointment(null);
    setShowThankYou(false);
  }, []);

  useEffect(() => {
    if (!showThankYou) {
      return;
    }
    const timeoutId = setTimeout(
      () => callbacksRef.current.onFinished(),
      THANK_YOU_DISMISS_MS,
    );
    return () => clearTimeout(timeoutId);
  }, [showThankYou, callbacksRef]);

  return {
    step,
    disappointment,
    showThankYou,
    selectOption,
    submitText,
    reset,
  };
};
